import { useStoryblokState } from '@cinch-nx/data-storyblok'
import {
  Head,
  JsonLdScript,
  renderWithResolver,
  StoreType,
} from '@cinch-nx/landing-ui-shared'
import {
  TrustpilotWidget,
  Wrapper,
  TrustpilotWidgetTemplates,
  TrustpilotScoreless,
} from '@cinch-nx/shared-ui'
import { Env, readFromEnv } from '@cinch-nx/environments'
import { useViewport } from '@cinch-labs/shared-util'

import { StoreIntro, HowItWorks } from './components'
import type { LandingFeatureStoreProps } from './get-static-props'
import styles from './landing-feature-store-page.module.css'
import { StoreDetailsWrapper } from './components/store-details-wrapper'
import { StoreFaqs } from './components/store-faqs'
import { capitaliseFirstLetter } from './utils/capitalise-first-letter'
import {
  generateFallbackDescription,
  generateFallbackTitle,
} from './data/landing-store-feature.data'
import { VehiclesInStock } from './components/vehicles-in-stock/vehicles-in-stock'
import { formatStoreToSchema } from '@cinch-nx/landing-ui-shared'
import { WhyCinch } from './components/why-cinch'
import cx from 'classnames'
import {
  useTrustpilotReviewCount,
  useTrustpilotScore,
} from '@cinch-nx/data-trustpilot'

export function LandingFeatureStorePage({
  story,
  generatedAt,
  preview,
  storeVehicles,
  storeType,
}: LandingFeatureStoreProps) {
  const { vw } = useViewport({
    updateOnResize: true,
    updateOnOrientationChange: true,
  })

  const isMobile = vw < 1024

  const shouldShowCarousel =
    storeType !== StoreType.clickAndCollect &&
    (isMobile || storeVehicles.length > 2) &&
    storeVehicles.length > 0

  const starRating = useTrustpilotScore()

  const shouldShowTrustPilotScoreless =
    readFromEnv(Env.ShowScorelessTrustpilot) === 'true'

  const shouldShowTruspilotScoreless = starRating
    ? shouldShowTrustPilotScoreless || starRating <= 4.2
    : shouldShowTrustPilotScoreless

  const numberOfReviews = useTrustpilotReviewCount()

  const { content } = useStoryblokState({ story, enableBridge: preview })
  const {
    storeIntro,
    storeHowCinchWorksComponent,
    storeFAQSection,
    mapInformation,
    address,
    phoneNumbers,
    openingHoursList,
    storeHero,
    storeWhyBuy,
  } = content
  const SEO = content?.SEO?.[0]

  const storeIntroTitle = storeIntro[0].title
  const storeIntroText = renderWithResolver(storeIntro[0].body)

  const howItWorksTitle = storeHowCinchWorksComponent[0].title
  const howCinchWorksList = storeHowCinchWorksComponent[0].howCinchWorksList
  const { titleAndText: whyBuyContent } = storeWhyBuy[0]

  const storeSlug = story.slug
  const storeName = capitaliseFirstLetter(storeSlug)
  const [storeAddress] = address
  const storeImageUrl = storeHero[0].image.filename

  const { title: faqTitle, faqs } = storeFAQSection[0]

  return (
    <>
      <JsonLdScript
        schema={formatStoreToSchema({
          storeSlug,
          storeAddress,
          mapInformation,
          phoneNumbers,
          openingHoursList,
          storeImageUrl,
        })}
        tagId="automotive business"
      />
      <Head
        {...SEO}
        generatedAt={generatedAt}
        fallbackTitle={generateFallbackTitle(storeName)}
        fallbackDescription={generateFallbackDescription(storeName)}
      />
      <article className={styles.root}>
        <Wrapper className={{ container: styles.storeWrapper }} isSlim>
          <StoreIntro
            title={storeIntroTitle}
            introText={storeIntroText}
            slug={storeSlug}
          />
        </Wrapper>
        <Wrapper
          backgroundColor="#f2f5ff"
          className={{ container: styles.howItWorksWrapper }}
          isSlim
        >
          <HowItWorks
            title={howItWorksTitle}
            howCinchWorksList={howCinchWorksList}
            slug={storeSlug}
            storeType={storeType}
          />
        </Wrapper>
        <Wrapper
          backgroundColor="#F9F9F9"
          className={{ inner: styles.trustpilotWrapper }}
        >
          {shouldShowTruspilotScoreless ? (
            <TrustpilotScoreless numberOfReviews={numberOfReviews} />
          ) : (
            <TrustpilotWidget
              height="24px"
              widget={
                isMobile
                  ? TrustpilotWidgetTemplates.microStar
                  : TrustpilotWidgetTemplates.microCombo
              }
            />
          )}
        </Wrapper>
        <Wrapper className={{ container: styles.storeWrapper }} isSlim>
          <StoreDetailsWrapper
            slug={storeName}
            latitude={Number.parseFloat(mapInformation[0]?.latitude)}
            longitude={Number.parseFloat(mapInformation[0]?.longitude)}
            address={storeAddress}
            phoneNumbers={phoneNumbers}
            openingHours={openingHoursList}
          />
        </Wrapper>
        <Wrapper className={{ container: styles.storeWrapper }} isSlim>
          <StoreFaqs title={faqTitle} faqs={faqs} />
        </Wrapper>
        <Wrapper
          backgroundColor="#f8f6ff"
          className={{
            container: cx(styles.storeWrapper, styles.whyCinchWrapper),
            inner: styles.storeWrapperInner,
          }}
        >
          <WhyCinch whyBuyContent={whyBuyContent} />
        </Wrapper>

        {shouldShowCarousel && (
          <Wrapper className={{ container: styles.storeWrapper }} isSlim>
            <VehiclesInStock slug={storeSlug} vehicles={storeVehicles} />
          </Wrapper>
        )}
      </article>
    </>
  )
}

export default LandingFeatureStorePage
